import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth, useSipPhone } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageLightbox from "@/components/ImageLightbox";
import LocationSharingButton from "@/components/LocationSharingButton";
import {
  Radio,
  MapPin,
  Phone,
  Clock,
  AlertTriangle,
  Eye,
  MessageSquare,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Image,
  Download,
  FileText,
  Film,
  Upload,
  Link,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Map,
  Mic,
  Video,
  Square,
  ChevronLeft,
  Maximize2,
  Minimize2,
  Play,
  Search,
  Flag
} from "lucide-react";

// Format time helper - New York timezone, TIME ONLY (no date per requirement #5)
const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { 
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format phone number helper (requirement #6 - display as 718-111-1111)
// Also handles international format like +1-718-111-1111 or +17181111111
const formatPhoneNumber = (value) => {
  if (!value) return "";
  
  // Check if it starts with + (international format)
  const hasPlus = value.startsWith('+');
  
  // Remove all non-digits
  let digits = value.replace(/\D/g, '');
  
  // If international format with country code 1 (US), handle it
  if (hasPlus && digits.startsWith('1') && digits.length > 10) {
    // Format as +1-XXX-XXX-XXXX
    const countryCode = digits.slice(0, 1);
    const areaCode = digits.slice(1, 4);
    const middle = digits.slice(4, 7);
    const last = digits.slice(7, 11);
    
    if (digits.length <= 1) return `+${countryCode}`;
    if (digits.length <= 4) return `+${countryCode}-${areaCode.slice(0, digits.length - 1)}`;
    if (digits.length <= 7) return `+${countryCode}-${areaCode}-${middle.slice(0, digits.length - 4)}`;
    return `+${countryCode}-${areaCode}-${middle}-${last}`;
  }
  
  // If just 11 digits starting with 1 (without +), still format with country code
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }
  
  // Standard 10-digit format: XXX-XXX-XXXX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Format elapsed time helper
const CALL_98_AUTO_LOG_AFTER_SECONDS = 3600;

function computeCall98RemainingSeconds(call) {
  if (!call || call.call_mode !== "call_98" || call.call_98_auto_log_cancelled) return null;
  const accumulated = Number(call.call_98_accumulated_seconds) || 0;
  const startedStr = call.call_98_started_at;
  let active = 0;
  if (startedStr) {
    const startedMs = Date.parse(startedStr);
    if (!Number.isNaN(startedMs)) {
      active = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    }
  }
  return Math.max(0, CALL_98_AUTO_LOG_AFTER_SECONDS - accumulated - active);
}

function Call98CountdownBadge({ call }) {
  const [remaining, setRemaining] = useState(() => computeCall98RemainingSeconds(call));

  useEffect(() => {
    setRemaining(computeCall98RemainingSeconds(call));
    const id = setInterval(() => {
      setRemaining(computeCall98RemainingSeconds(call));
    }, 1000);
    return () => clearInterval(id);
  }, [call]);

  if (remaining === null) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expired = remaining === 0;
  return (
    <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center ${expired ? "bg-amber-600" : "bg-green-800"} text-white`}>
      Auto-log {expired ? "pending" : `in ${mm}:${ss}`}
    </Badge>
  );
}

const formatElapsed = (dateString, currentTime) => {
  if (!dateString) return "";
  const created = new Date(dateString);
  const diff = currentTime - created;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
};

// Call List Item Component
function CallListItem({ call, selectedCall, setSelectedCall, myUnit, currentTime, onMarkRead, callIndex = 0, totalCalls = 1 }) {
  const [showUnits, setShowUnits] = useState(false);
  const isPriority = call.priority === "priority";
  const isSelected = selectedCall?.call_id === call.call_id;
  const isAssigned = myUnit && call.assigned_units?.includes(myUnit);
  const isConfirmed = call.confirmation_status === "confirmed";
  const isUnconfirmed = call.confirmation_status === "unconfirmed";
  const isNoFurther = call.no_further === true;
  const isUnitsRequired = call.units_required === true;
  const isSlowdown = call.slowdown === true;
  const isForthwith = call.forthwith === true;
  const isLiveCall = call.call_mode === "live_call";
  const myUnit98Status = myUnit && call.unit_statuses?.[myUnit]?.status_98;
  const isCall98 = call.call_mode === "call_98" || (isAssigned && myUnit98Status);
  const hasArmedPerp = call.perp_descriptions?.some(p => p.has_weapon);
  const isOddCall = callIndex % 2 === 1;

  // Color bands for visual distinction (cycle through colors)
  const colorBands = ['border-l-blue-500', 'border-l-emerald-500', 'border-l-purple-500', 'border-l-orange-500', 'border-l-pink-500'];
  const bandColor = colorBands[callIndex % colorBands.length];

  return (
    <>
      {/* Divider between calls (not before first call) */}
      {callIndex > 0 && totalCalls > 1 && (
        <div className="flex items-center gap-2 py-2 px-3 bg-slate-300/50 dark:bg-slate-700/50">
          <div className="flex-1 h-px bg-slate-400 dark:bg-slate-500"></div>
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">CALL {callIndex + 1} of {totalCalls}</span>
          <div className="flex-1 h-px bg-slate-400 dark:bg-slate-500"></div>
        </div>
      )}
      {/* First call indicator */}
      {callIndex === 0 && totalCalls > 1 && (
        <div className="flex items-center gap-2 py-1 px-3 bg-slate-200/50 dark:bg-slate-800/50">
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">CALL 1 of {totalCalls}</span>
        </div>
      )}
      <div
        onClick={() => setSelectedCall(call)}
        className={`
          p-3 cursor-pointer transition-all overflow-hidden border-l-4
          ${isCall98 ? `opacity-70 bg-green-400/40 dark:bg-green-700/10 ${bandColor}` :
            isSelected && isPriority && !isNoFurther ? "bg-red-500/20 dark:bg-red-900/40 border-l-red-500" :
            isSelected ? "bg-blue-600/20 dark:bg-blue-600/30 border-l-blue-500" :
            isPriority && !isNoFurther ? `bg-red-500/10 dark:bg-red-900/30 hover:bg-red-500/20 dark:hover:bg-red-900/40 border-l-red-500` :
            isOddCall ? `bg-slate-100/50 dark:bg-slate-800/30 hover:bg-slate-200/70 dark:hover:bg-slate-700/50 ${bandColor}` :
            `bg-transparent hover:bg-slate-200/50 dark:hover:bg-slate-800/50 ${bandColor}`}
        `}
      >
      <div className="flex items-start justify-between gap-2 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* First line: Badges */}
          <div className="flex flex-wrap gap-1 mb-2">
            {isPriority && (
              <Badge variant="destructive" className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center whitespace-nowrap ${isCall98 ? '' : 'animate-pulse-fast'}`}>PRIORITY</Badge>
            )}
            {isConfirmed && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-green-600 text-white whitespace-nowrap rounded">CONFIRMED</Badge>
            )}
            {isUnconfirmed && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-amber-500 text-black font-bold border border-amber-600 whitespace-nowrap rounded">UNCONFIRMED</Badge>
            )}
            {isUnitsRequired && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-500 text-white whitespace-nowrap">UNITS REQ'D</Badge>
            )}
            {isSlowdown && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-500 text-white whitespace-nowrap">SLOWDOWN</Badge>
            )}
            {isNoFurther && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-slate-600 text-white whitespace-nowrap">NO FURTHER</Badge>
            )}
            {isForthwith && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-pink-500 text-white whitespace-nowrap">FORTHWITH</Badge>
            )}
            {isLiveCall && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-blue-600 text-white whitespace-nowrap">LIVE CALL</Badge>
            )}
            {isCall98 && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-green-600 text-white whitespace-nowrap">CALL 98</Badge>
            )}
            {call.transmitted_10_85 && (
              <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-orange-600 text-white whitespace-nowrap ${isCall98 ? '' : 'animate-pulse-fast'}`}>10-85</Badge>
            )}
            {call.transmitted_10_13 && (
              <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-red-600 text-white whitespace-nowrap ${isCall98 ? '' : 'animate-pulse-fast'}`}>10-13</Badge>
            )}
            {hasArmedPerp && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-red-700 text-white whitespace-nowrap">⚠ ARMED</Badge>
            )}
          </div>
          {/* Second line: Call type - BOLD UPPERCASE */}
          <div className="font-black text-sm text-slate-800 dark:text-slate-200 uppercase mb-1">
            {call.call_type || "UNKNOWN TYPE"}
            {call.secondary_call_type && (
              <span className="font-black text-sm text-slate-800 dark:text-slate-200 uppercase ml-1">/ {call.secondary_call_type}</span>
            )}
          </div>
          {/* Location */}
          <div className="text-sm text-slate-600 dark:text-slate-300">
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="break-words">
                {call.location || "Unknown"}
                {call.location_notes && <span className="text-slate-500 dark:text-slate-400 italic"> - {call.location_notes}</span>}
              </span>
            </div>
            {call.cross_streets && (
              <div className="text-emerald-600 dark:text-emerald-400 ml-5 text-xs font-medium">✕ {call.cross_streets}</div>
            )}
          </div>
          {/* Call Note - show first line/paragraph only */}
          {call.call_note && (
            <div className="mt-1.5 text-xs text-slate-600 dark:text-slate-400 italic">
              {call.call_note.split('\n')[0]}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatTime(call.created_at)}</div>
          <div className="text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">{formatElapsed(call.created_at, currentTime)}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-500 font-mono">{call.call_id}</div>
          {isAssigned && (
            <Badge variant="secondary" className="mt-1 text-[10px] px-1 py-0.5 bg-green-600 text-white rounded">RESPONDING</Badge>
          )}
        </div>
      </div>
      
      {/* Assigned Units */}
      {call.assigned_units?.length > 0 && (
        <div className="mt-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newShowUnits = !showUnits;
              setShowUnits(newShowUnits);
              // Trigger read receipt when showing units
              if (newShowUnits && onMarkRead) {
                onMarkRead(call.call_id);
              }
            }}
            className="text-[10px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
          >
            <Users className="w-3 h-3" />
            {call.assigned_units.length} unit{call.assigned_units.length !== 1 ? 's' : ''}
            {showUnits ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showUnits && (
            <div className="grid grid-cols-3 gap-1 mt-1">
              {[...call.assigned_units].sort((a, b) => {
                const numA = parseInt(a.replace(/\D/g, '')) || 0;
                const numB = parseInt(b.replace(/\D/g, '')) || 0;
                if (numA !== numB) return numA - numB;
                return a.localeCompare(b);
              }).map(unit => {
                const isMe = unit === myUnit;
                return (
                  <Badge
                    key={unit}
                    variant="outline"
                    className={`text-[10px] px-1 py-0.5 flex items-center justify-center truncate ${isMe ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300' : 'bg-white dark:bg-slate-700 border-slate-400 dark:border-slate-600 text-slate-700 dark:text-slate-300'}`}
                  >
                    {unit}
                    {call.unit_statuses?.[unit]?.status_standby ? (
                      <span className="ml-1 text-amber-600 dark:text-amber-400 font-semibold">STBY</span>
                    ) : (
                      <>
                        {call.unit_statuses?.[unit]?.status_84 && <span className="ml-1 text-blue-600 dark:text-blue-400">84</span>}
                        {call.unit_statuses?.[unit]?.status_98 && <span className="ml-1 text-green-600 dark:text-green-400">98</span>}
                      </>
                    )}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}

// Call Detail Component (Read-Only View)
function CallDetail({ call, myUnit, user, isUploadingMedia, uploadProgress, onMediaUpload, onDownloadMedia, setSelectedCall, onRespond, onUnrespond, onUpdateStatus, currentTime, updatesSortOrder, onUpdatesSortChange, locationSortOrder, onLocationSortChange, mediaFiles, loadingMedia, setSelectedCallMedia, onNavigateToProfile, listMinimized, setListMinimized, handlePhoneClick }) {
  const isAssigned = myUnit && call.assigned_units?.includes(myUnit);
  const myStatus = myUnit ? call.unit_statuses?.[myUnit] : null;
  const isConfirmed = call.confirmation_status === "confirmed";
  const isUnconfirmed = call.confirmation_status === "unconfirmed";
  const isNoFurther = call.no_further === true;
  const isUnitsRequired = call.units_required === true;
  const isSlowdown = call.slowdown === true;
  const isForthwith = call.forthwith === true;
  const isCall98 = call.call_mode === "call_98" || (isAssigned && call.unit_statuses?.[myUnit]?.status_98);
  const hasArmedPerp = call.perp_descriptions?.some(p => p.has_weapon);
  const [showProfilePopup, setShowProfilePopup] = useState(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [unitNoteText, setUnitNoteText] = useState("");
  const [submittingUnitNote, setSubmittingUnitNote] = useState(false);
  const [showUnitNotes, setShowUnitNotes] = useState(false);
  const [updatesSearch, setUpdatesSearch] = useState("");

  // Section visibility toggles
  const [showLocationInfo, setShowLocationInfo] = useState(true);
  const [showJobBody, setShowJobBody] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showPerps, setShowPerps] = useState(true);
  const [collapsedPerps, setCollapsedPerps] = useState({});
  // Per-perp Updates section collapse — independent of the whole-card collapse.
  const [collapsedPerpUpdates, setCollapsedPerpUpdates] = useState({});
  const [showCallbacks, setShowCallbacks] = useState(true);
  const [showUpdatesTimeline, setShowUpdatesTimeline] = useState(true);
  // Combined Location Updates list defaults: shown on desktop, hidden on
  // mobile so the panel stays compact on small screens.
  const [showLocationUpdates, setShowLocationUpdates] = useState(
    typeof window === 'undefined' || window.innerWidth >= 768
  );
  // Per-location/per-perp updates inside Location Info — orig + additionals
  // default to shown everywhere; perp default to hidden everywhere.
  const [expandedLocUpdates, setExpandedLocUpdates] = useState({});
  const isLocUpdatesShown = (key, defaultShown) => (
    expandedLocUpdates[key] === undefined ? !!defaultShown : expandedLocUpdates[key]
  );
  const toggleLocUpdates = (key, defaultShown) => setExpandedLocUpdates(prev => ({ ...prev, [key]: !(prev[key] === undefined ? !!defaultShown : prev[key]) }));
  const [updatesTab, setUpdatesTab] = useState("timeline"); // 'timeline' | 'all'
  const [showRespondingUnits, setShowRespondingUnits] = useState(true);
  const [showMedia, setShowMedia] = useState(true);
  const [responseTab, setResponseTab] = useState("all");

  // Building Codes + Known Locations for Location card badges and tap-to-map.
  const [buildingCodes, setBuildingCodes] = useState([]);
  const [knownLocations, setKnownLocations] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/building-codes`, { withCredentials: true })
      .then(r => { if (!cancelled) setBuildingCodes(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
      .catch(() => { if (!cancelled) setBuildingCodes([]); });
    axios.get(`${API}/known-locations`, { withCredentials: true })
      .then(r => { if (!cancelled) setKnownLocations(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
      .catch(() => { if (!cancelled) setKnownLocations([]); });
    return () => { cancelled = true; };
  }, []);
  const normalizeAddressForMatch = (addr) => {
    if (!addr) return "";
    const SUFFIX = new Set([
      "st","street","ave","avenue","av","blvd","boulevard","rd","road","pkwy","parkway",
      "dr","drive","ln","lane","pl","place","ct","court","ter","terrace","way","hwy",
      "highway","sq","square","cir","circle","trl","trail","loop","aly","alley"
    ]);
    const DIRECTIONALS = new Set(["n","s","e","w","north","south","east","west","ne","nw","se","sw"]);
    const head = String(addr).split(",")[0].toLowerCase();
    const tokens = head.replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return "";
    const numMatch = tokens[0].match(/^(\d+)[a-z]?$/);
    if (!numMatch) return "";
    const num = numMatch[1];
    let streetWord = "";
    for (let i = 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (DIRECTIONALS.has(t) || SUFFIX.has(t)) continue;
      streetWord = t;
      break;
    }
    if (!streetWord) return "";
    return `${num} ${streetWord}`;
  };
  const findBuildingCode = (addr) => {
    const key = normalizeAddressForMatch(addr);
    if (!key || !buildingCodes?.length) return null;
    const hit = buildingCodes.find(b => normalizeAddressForMatch(b.address) === key);
    return hit?.building_code || null;
  };
  const findKnownLocationCoords = (addr) => {
    if (!addr || !knownLocations?.length) return null;
    const key = normalizeAddressForMatch(addr);
    let hit = key ? knownLocations.find(k => normalizeAddressForMatch(k.address) === key) : null;
    if (!hit) {
      const trimmed = String(addr).trim().toLowerCase();
      hit = knownLocations.find(k => (k.name || "").trim().toLowerCase() === trimmed);
    }
    if (!hit || hit.latitude == null || hit.longitude == null) return null;
    return { lat: hit.latitude, lng: hit.longitude };
  };
  const mapHrefFor = (addr) => {
    const coords = findKnownLocationCoords(addr);
    if (coords) {
      return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((addr || "") + ", Brooklyn, NY")}`;
  };

  // Lightbox state for viewing media
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [lightboxMediaType, setLightboxMediaType] = useState(null);
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Helper to open media in lightbox
  const openLightbox = (src, type) => {
    setLightboxMedia(src);
    setLightboxMediaType(type);
  };
  
  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Upload the audio
        const formData = new FormData();
        formData.append('file', audioBlob, `recording_${Date.now()}.webm`);
        try {
          const uploadResponse = await axios.post(
            `${API}/upload`,
            formData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (uploadResponse.data.url) {
            await axios.post(
              `${API}/live-calls/${call.call_id}/media`,
              { media_files: [uploadResponse.data.url] },
              { withCredentials: true }
            );
            setSelectedCallMedia(prev => [...prev, uploadResponse.data.url]);
            toast.success('Audio recording uploaded');
          }
        } catch (err) {
          toast.error('Failed to upload audio recording');
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started - tap again to stop');
    } catch (err) {
      toast.error('Could not access microphone');
    }
  };
  
  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);
  
  // Get all updates for the "all updates" section
  const allUpdates = call.updates || [];
  
  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-4">
        {/* Header - Scrolls with content */}
        <div className="pb-4 border-b border-slate-200 dark:border-slate-800">
          {/* Call Type with Back Button and Maximize Toggle */}
          <div className="flex items-center gap-2 mb-2">
            {/* Mobile: Back to list */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCall(null)}
              className="lg:hidden h-7 px-2 text-xs"
              data-testid="back-to-list-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {/* Desktop: Show list button (when minimized) */}
            {listMinimized && (
              <Button
                variant="ghost"
                size="sm"
                className="hidden lg:flex h-7 px-2 text-xs text-slate-400 hover:text-slate-200"
                onClick={() => setListMinimized(false)}
                title="Show call list"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex-1">
              {call.call_type || "Unknown Type"}
              {call.secondary_call_type && (
                <span className="text-lg font-semibold text-slate-800 dark:text-slate-200 ml-2">/ {call.secondary_call_type}</span>
              )}
            </h2>
            {/* Desktop: Maximize/Minimize toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="hidden lg:flex h-7 px-2 text-xs text-slate-400 hover:text-slate-200"
              onClick={() => setListMinimized(!listMinimized)}
              title={listMinimized ? "Show call list" : "Maximize call view"}
            >
              {listMinimized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>


          {/* Time and Duration - Own line with AM/PM */}
          <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">
            {formatTime(call.created_at)} — {formatElapsed(call.created_at, currentTime)} ago
          </div>

          {/* Badges - Grid with min 3 per row */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5 mb-2">
            {call.priority === "priority" && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center">PRIORITY</Badge>
            )}
            {call.transmitted_10_85 && (
              <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-orange-600 text-white ${isCall98 ? '' : 'animate-pulse-fast'}`}>10-85</Badge>
            )}
            {call.transmitted_10_13 && (
              <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-red-600 text-white ${isCall98 ? '' : 'animate-pulse-fast'}`}>10-13</Badge>
            )}
            {isConfirmed && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-green-100 dark:bg-green-600/30 text-green-700 dark:text-green-400 border-green-500">CONFIRMED</Badge>
            )}
            {isUnconfirmed && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-amber-500 text-black font-bold border-amber-600">UNCONFIRMED</Badge>
            )}
            {isUnitsRequired && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-500 text-white">UNITS REQ'D</Badge>
            )}
            {isSlowdown && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-500 text-white">SLOWDOWN</Badge>
            )}
            {isNoFurther && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-slate-600 text-white">NO FURTHER</Badge>
            )}
            {isForthwith && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-pink-500 text-white">FORTHWITH</Badge>
            )}
            {call.call_mode === "live_call" && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-blue-600 text-white">LIVE CALL</Badge>
            )}
            {(call.call_mode === "call_98" || (isAssigned && call.unit_statuses?.[myUnit]?.status_98)) && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-green-600 text-white">CALL 98</Badge>
            )}
            {hasArmedPerp && (
              <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-red-700 text-white">⚠ ARMED</Badge>
            )}
          </div>
          
          {/* Response Buttons */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {myUnit ? (
              <>
                {user?.can_self_respond === false ? (
                  <span className="text-xs text-slate-500 italic">Self-respond disabled by admin</span>
                ) : call.self_respond_enabled === false ? (
                  <span className="text-xs text-slate-500 italic">
                    Self-respond disabled for this call — only dispatch can {isAssigned ? "unassign" : "assign"}
                  </span>
                ) : isAssigned ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onUnrespond(call.call_id)}
                      className="bg-red-900/30 border-red-500 text-red-400 hover:bg-red-900/50"
                    >
                      <UserX className="w-4 h-4 mr-1" />
                      Cancel Responding
                    </Button>

                    {/* 84/98 Status Buttons - Only when assigned */}
                    <div className="flex items-center gap-2 ml-2 border-l border-slate-200 dark:border-slate-700 pl-2">
                      <Button
                        variant={myStatus?.status_84 ? "default" : "outline"}
                        size="sm"
                        onClick={() => onUpdateStatus(call.call_id, !myStatus?.status_84, myStatus?.status_98 || false)}
                        className={myStatus?.status_84 ? "bg-blue-600 hover:bg-blue-700" : ""}
                      >
                        84
                      </Button>
                      <Button
                        variant={myStatus?.status_98 ? "default" : "outline"}
                        size="sm"
                        onClick={() => onUpdateStatus(call.call_id, myStatus?.status_84 || false, !myStatus?.status_98)}
                        className={myStatus?.status_98 ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        98
                      </Button>
                    </div>

                    {/* Location Sharing Button - only show if allowed */}
                    {user?.allow_location_sharing !== false && (
                      <LocationSharingButton
                        liveCallId={call.call_id}
                        compact={true}
                        className="ml-2"
                      />
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onRespond(call.call_id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Respond
                    </Button>

                    {/* Location Sharing Button - only show if allowed */}
                    {user?.allow_location_sharing !== false && (
                      <LocationSharingButton
                        liveCallId={call.call_id}
                        compact={true}
                      />
                    )}
                  </div>
                )}
                {/* Unit number removed per requirement #4 */}
              </>
            ) : (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center">No unit assigned to your account</Badge>
            )}
          </div>
        </div>

        {/* Quick Filter Tabs */}
        <div className="max-w-2xl">
          <div className="flex flex-wrap gap-1">
            {[
              { key: "all", label: "All" },
              { key: "narrative", label: "Narrative" },
              { key: "perps", label: "Perps" },
              { key: "cvcb", label: "CV/CB" },
              { key: "units", label: "Units" },
              { key: "location", label: "Location" },
              { key: "media", label: "Media" },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setResponseTab(responseTab === tab.key ? "all" : tab.key)}
                className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${
                  responseTab === tab.key
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-slate-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 1. Job Narrative & Callbacks */}
        {(responseTab === "all" || responseTab === "narrative" || responseTab === "cvcb") && (call.call_note || (isAssigned && (call.callback_phone || call.callback_numbers?.length > 0 || call.victims?.length > 0 || call.primary_victim_name || call.witnesses?.length > 0 || call.primary_witness_name))) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {(responseTab === "all" || responseTab === "narrative") && call.call_note && (
              <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    Job Narrative
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowJobBody(!showJobBody)}>
                      <Eye className="w-3 h-3 mr-1" />{showJobBody ? "Hide" : "Show"}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showJobBody && (
                <CardContent className="pt-0">
                  <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{call.call_note}</p>
                </CardContent>
                )}
              </Card>
            )}
            {(responseTab === "all" || responseTab === "cvcb") && isAssigned && (call.callback_phone || call.callback_numbers?.length > 0 || call.victims?.length > 0 || call.primary_victim_name || call.witnesses?.length > 0 || call.primary_witness_name) && (
              <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Callbacks
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowCallbacks(!showCallbacks)}>
                      <Eye className="w-3 h-3 mr-1" />{showCallbacks ? "Hide" : "Show"}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showCallbacks && (
                <CardContent className="pt-0 space-y-2">
                  {call.callback_phone && !call.callback_numbers?.some(c => c.number === call.callback_phone) && (
                    <div className="p-2 bg-slate-100 dark:bg-slate-900/50 rounded">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handlePhoneClick?.(call.callback_phone)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                          {formatPhoneNumber(call.callback_phone)}
                        </button>
                        {call.cv_or_third_party && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center">{call.cv_or_third_party === 'cv' ? 'CV' : '3rd Party'}</Badge>
                        )}
                        {call.caller_has_visual && <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-emerald-600 text-white">VISUAL</Badge>}
                        {call.callback_phone_contact_status && <Flag className="w-3.5 h-3.5 text-yellow-500" />}
                      </div>
                      {call.callback_phone_contact_name && (
                        <div className="text-xs text-yellow-500 mt-1 pl-0.5">{call.callback_phone_contact_name}</div>
                      )}
                    </div>
                  )}
                  {call.callback_numbers?.map((cb, idx) => (
                    <div key={cb.callback_id || idx} className="p-2 bg-slate-100 dark:bg-slate-900/50 rounded">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handlePhoneClick?.(cb.number)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                          {formatPhoneNumber(cb.number)}
                        </button>
                        {cb.label && <span className="text-xs text-slate-500">({cb.label})</span>}
                        {(cb.caller_type === 'cv' || cb.caller_type === '3rd_party') && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center">{cb.caller_type === '3rd_party' ? '3rd Party' : 'CV'}</Badge>
                        )}
                        {cb.has_visual && <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-emerald-600 text-white">VISUAL</Badge>}
                        {cb.contact_status && <Flag className="w-3.5 h-3.5 text-yellow-500" />}
                      </div>
                      {cb.contact_name && (
                        <div className="text-xs text-yellow-500 mt-1 pl-0.5">{cb.contact_name}</div>
                      )}
                    </div>
                  ))}

                  {/* Victims */}
                  {(call.victims?.length > 0 || call.primary_victim_name) && (
                    <>
                      <div className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-[10px] uppercase text-emerald-600 dark:text-emerald-400 font-semibold mb-1">Victim Contact</p>
                      </div>
                      {call.primary_victim_name && !call.victims?.some(v => v.name === call.primary_victim_name) && (
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded">
                          <div className="flex items-center gap-2">
                            {call.primary_victim_contact ? (
                              <button onClick={() => handlePhoneClick?.(call.primary_victim_contact)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                {formatPhoneNumber(call.primary_victim_contact)}
                              </button>
                            ) : null}
                            <Badge className="text-[10px] px-1.5 py-0.5 bg-emerald-600 text-white">VICTIM</Badge>
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">{call.primary_victim_name}</div>
                        </div>
                      )}
                      {call.victims?.map((v, idx) => (
                        <div key={idx} className="p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded">
                          <div className="flex items-center gap-2">
                            {(v.contact || v.phone1) ? (
                              <button onClick={() => handlePhoneClick?.(v.contact || v.phone1)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                {formatPhoneNumber(v.contact || v.phone1)}
                              </button>
                            ) : null}
                            <Badge className="text-[10px] px-1.5 py-0.5 bg-emerald-600 text-white">VICTIM</Badge>
                          </div>
                          {v.name && <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">{v.name}</div>}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Witnesses */}
                  {(call.witnesses?.length > 0 || call.primary_witness_name) && (
                    <>
                      <div className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-[10px] uppercase text-sky-600 dark:text-sky-400 font-semibold mb-1">Witness Contact</p>
                      </div>
                      {call.primary_witness_name && !call.witnesses?.some(w => w.name === call.primary_witness_name) && (
                        <div className="p-2 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/50 rounded">
                          <div className="flex items-center gap-2">
                            {call.primary_witness_contact ? (
                              <button onClick={() => handlePhoneClick?.(call.primary_witness_contact)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                {formatPhoneNumber(call.primary_witness_contact)}
                              </button>
                            ) : null}
                            <Badge className="text-[10px] px-1.5 py-0.5 bg-sky-600 text-white">WITNESS</Badge>
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">{call.primary_witness_name}</div>
                        </div>
                      )}
                      {call.witnesses?.map((w, idx) => (
                        <div key={idx} className="p-2 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/50 rounded">
                          <div className="flex items-center gap-2">
                            {(w.contact || w.contact_info) ? (
                              <button onClick={() => handlePhoneClick?.(w.contact || w.contact_info)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                {formatPhoneNumber(w.contact || w.contact_info)}
                              </button>
                            ) : null}
                            <Badge className="text-[10px] px-1.5 py-0.5 bg-sky-600 text-white">WITNESS</Badge>
                          </div>
                          {w.name && <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">{w.name}</div>}
                        </div>
                      ))}
                    </>
                  )}
                </CardContent>
                )}
              </Card>
            )}
          </div>
        )}

        {/* 2. Location Info Card */}
        <Card className={`bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-2xl ${responseTab !== "all" && responseTab !== "location" ? "hidden" : ""}`}>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                Location Info
              </div>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowLocationInfo(!showLocationInfo)}>
                <Eye className="w-3 h-3 mr-1" />{showLocationInfo ? "Hide" : "Show"}
              </Button>
            </CardTitle>
          </CardHeader>
          {showLocationInfo && (
          <CardContent className="pt-0 space-y-2">
            {/* Main Location - Single box like Live Calls */}
            <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Original{call.location_title ? `/${call.location_title}` : ""}</p>
                {(() => {
                  const bc = findBuildingCode(call.location);
                  return bc ? (
                    <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                  ) : null;
                })()}
              </div>
              <div className="flex items-start gap-1">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                {call.location ? (
                  <a
                    href={mapHrefFor(call.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    {call.location}
                  </a>
                ) : (
                  <span className="text-sm text-slate-500">Not specified</span>
                )}
              </div>
              {call.cross_streets && (
                <div className="text-xs text-emerald-600 dark:text-emerald-400 ml-4 mt-0.5">({call.cross_streets})</div>
              )}
              {call.location_notes && (
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 ml-4">{call.location_notes}</p>
              )}
              {call.direction_of_flight && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 ml-4 font-medium">→ {call.direction_of_flight}</p>
              )}
              {/* Latest location/direction update tagged to the original location */}
              {(() => {
                const updates = (call.updates || [])
                  .filter(u => (u.updated_location || u.updated_direction) && !u.perp_id && !(u.location_label || "").trim())
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                if (updates.length === 0) return null;
                const expanded = isLocUpdatesShown('orig', true);
                return (
                  <>
                    <div className="ml-4 mt-1 pt-1 border-t border-slate-200 dark:border-slate-700/60">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70 uppercase font-medium">Updates ({updates.length})</p>
                        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => toggleLocUpdates('orig', true)}>
                          <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                        </Button>
                      </div>
                      {expanded && (
                        <div className="space-y-1 mt-1">
                          {updates.map((u, i) => (
                            <div key={u.update_id || i} className="text-[11px] bg-amber-50 dark:bg-amber-900/20 rounded px-1.5 py-1">
                              <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">{formatTime(u.timestamp)} · {u.updated_by}</div>
                              {u.updated_location && (<div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 dark:hover:text-blue-400">{u.updated_location}</a></div>)}
                              {u.updated_direction && (<div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {/* Additional Locations */}
            {call.additional_locations?.length > 0 && call.additional_locations.map((loc, idx) => (
              <div key={idx} className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  {loc.title && <p className="text-[10px] text-blue-600 dark:text-blue-300 font-semibold uppercase">{loc.title}</p>}
                  {(() => {
                    const bc = findBuildingCode(loc.location);
                    return bc ? (
                      <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                    ) : null;
                  })()}
                </div>
                <div className="flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                  {loc.location ? (
                    <a
                      href={mapHrefFor(loc.location)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >{loc.location}</a>
                  ) : <span className="text-sm text-slate-500">Not specified</span>}
                </div>
                {loc.cross_streets && <div className="text-xs text-emerald-600 dark:text-emerald-400 ml-4 mt-0.5">({loc.cross_streets})</div>}
                {loc.location_notes && <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 ml-4">{loc.location_notes}</p>}
                {loc.direction_of_flight && <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 ml-4 font-medium">→ {loc.direction_of_flight}</p>}
                {/* Latest location/direction update tagged to this additional location */}
                {(() => {
                  const label = loc.title || `Additional #${idx + 1}`;
                  const updates = (call.updates || [])
                    .filter(u => (u.updated_location || u.updated_direction) && !u.perp_id && (u.location_label || "") === label)
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                  if (updates.length === 0) return null;
                  const key = `add-${idx}`;
                  const expanded = isLocUpdatesShown(key, true);
                  return (
                    <>
                      <div className="ml-4 mt-1 pt-1 border-t border-slate-200 dark:border-slate-700/60">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 uppercase font-medium">Updates ({updates.length})</p>
                          <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => toggleLocUpdates(key, true)}>
                            <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                          </Button>
                        </div>
                        {expanded && (
                          <div className="space-y-1 mt-1">
                            {updates.map((u, i) => (
                              <div key={u.update_id || i} className="text-[11px] bg-blue-50 dark:bg-blue-900/20 rounded px-1.5 py-1">
                                <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">{formatTime(u.timestamp)} · {u.updated_by}</div>
                                {u.updated_location && (<div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 dark:hover:text-blue-400">{u.updated_location}</a></div>)}
                                {u.updated_direction && (<div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
            {/* Perp Latest Locations — one card per perp that has a current location or direction */}
            {(call.perp_descriptions || []).map((perp, idx) => {
              if (!perp.current_location && !perp.direction_of_flight) return null;
              const descBits = [];
              // Sex + race render without labels and slash-joined to match the
              // WhatsApp perp blast format ('Male / Black').
              const sexRace = [perp.sex, perp.race].filter(Boolean).join(' / ');
              if (sexRace) descBits.push(sexRace);
              if (perp.age) descBits.push(`age/${perp.age}`);
              const clothingBits = [
                perp.hat_color_style && `hat/${perp.hat_color_style}`,
                perp.shirt && `shirt/${perp.shirt}`,
                perp.jacket_coat && `jacket/${perp.jacket_coat}`,
                perp.pants && `pants/${perp.pants}`,
                perp.shoes && `shoes/${perp.shoes}`,
                perp.clothing && `clothing/${perp.clothing}`,
              ].filter(Boolean);
              const descLine = descBits.join(" | ");
              const clothingLine = clothingBits.join(" | ");
              const locTime = perp.location_updated_at ? new Date(perp.location_updated_at) : null;
              const dirTime = perp.direction_updated_at ? new Date(perp.direction_updated_at) : null;
              const sameUpdate = locTime && dirTime && Math.abs(locTime.getTime() - dirTime.getTime()) < 2000;
              const locationIsNewer = sameUpdate || (locTime && dirTime ? locTime > dirTime : locTime && !dirTime);
              const directionIsNewer = sameUpdate || (dirTime && locTime ? dirTime > locTime : dirTime && !locTime);
              return (
                <div key={`perpLoc_${perp.perp_id || idx}`} className="p-2 bg-purple-50 dark:bg-purple-950/30 rounded border border-purple-200 dark:border-purple-800/50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-purple-600 dark:text-purple-300 font-semibold uppercase">
                        Perp #{idx + 1}{perp.linked_profile_name ? ` — ${perp.linked_profile_name}` : ''}
                      </span>
                      {(() => {
                        const bc = findBuildingCode(perp.current_location);
                        return bc ? (
                          <Badge className="text-[10px] bg-cyan-700 text-white border-0">🏢 {bc}</Badge>
                        ) : null;
                      })()}
                      {(() => {
                        // Show only the most recently set of HOLDING / DETAINED /
                        // ARRESTED / VISUAL — same rule as LiveCalls. ARMED and
                        // POSITIVE ID render independently.
                        const candidates = [
                          (perp.was_arrested || perp.is_arrested) && { label: 'ARRESTED', cls: 'bg-emerald-700', ts: perp.was_arrested_at || perp.arrest_updated_at },
                          perp.is_holding && { label: 'HOLDING', cls: 'bg-amber-700', ts: perp.is_holding_at },
                          perp.is_detained && { label: 'DETAINED', cls: 'bg-orange-700', ts: perp.is_detained_at },
                          perp.is_visual && { label: 'VISUAL', cls: 'bg-cyan-600', ts: perp.is_visual_at },
                        ].filter(Boolean);
                        if (candidates.length === 0) return null;
                        candidates.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
                        const latest = candidates[0];
                        return <Badge className={`text-[10px] ${latest.cls} text-white border-0`}>{latest.label}</Badge>;
                      })()}
                      {perp.is_positive_id && <Badge className="text-[10px] bg-emerald-600 text-white border-0">POSITIVE ID</Badge>}
                      {perp.has_weapon && <Badge variant="destructive" className="text-[10px]">ARMED</Badge>}
                    </div>
                    {(perp.location_updated_at || perp.direction_updated_at) && (
                      <span className="text-[10px] text-slate-500 font-mono">
                        {formatTime(perp.location_updated_at || perp.direction_updated_at)}
                      </span>
                    )}
                  </div>
                  {(descLine || clothingLine) && (
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
                      {descLine}{descLine && clothingLine ? ' — ' : ''}{clothingLine}
                    </p>
                  )}
                  {perp.current_location && (
                    <div className={`text-sm mt-1 px-1.5 py-0.5 rounded flex flex-wrap items-center gap-1 ${locationIsNewer ? 'bg-green-500/20 text-green-700 dark:text-green-400 font-medium border border-green-500/50' : 'text-slate-800 dark:text-slate-200 font-medium'}`}>
                      <span className="flex-1 min-w-0 break-words">
                        📍 <a
                          href={mapHrefFor(perp.current_location)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {perp.current_location}
                        </a>
                      </span>
                      {locationIsNewer && !sameUpdate && (
                        <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>
                      )}
                    </div>
                  )}
                  {perp.direction_of_flight && (
                    <div className={`text-sm mt-1 px-1.5 py-0.5 rounded flex flex-wrap items-center gap-1 ${directionIsNewer ? 'bg-green-500/20 text-green-700 dark:text-green-400 font-medium border border-green-500/50' : 'text-amber-700 dark:text-amber-400 font-medium'}`}>
                      <span className="flex-1 min-w-0 break-words">→ Direction: {perp.direction_of_flight}</span>
                      {directionIsNewer && !sameUpdate && (
                        <Badge className="bg-green-600 text-white text-[9px] px-1 py-0">LATEST</Badge>
                      )}
                    </div>
                  )}
                  {/* Per-perp updates list — defaults to hidden so the perp box
                      stays compact. */}
                  {(() => {
                    const perpUpdates = (call.updates || [])
                      .filter(u => (u.updated_location || u.updated_direction) && u.perp_id === perp.perp_id)
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                    if (perpUpdates.length === 0) return null;
                    const key = `perp-${perp.perp_id || idx}`;
                    const expanded = isLocUpdatesShown(key, false);
                    return (
                      <div className="mt-1 pt-1 border-t border-slate-200 dark:border-slate-700/60">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-purple-600/70 dark:text-purple-400/70 uppercase font-medium">Updates ({perpUpdates.length})</p>
                          <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => toggleLocUpdates(key, false)}>
                            <Eye className="w-3 h-3 mr-1" />{expanded ? 'Hide' : 'Show'}
                          </Button>
                        </div>
                        {expanded && (
                          <div className="space-y-1 mt-1">
                            {perpUpdates.map((u, i) => (
                              <div key={u.update_id || i} className="text-[11px] bg-purple-50 dark:bg-purple-900/20 rounded px-1.5 py-1">
                                <div className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">{formatTime(u.timestamp)} · {u.updated_by}</div>
                                {u.updated_location && (<div className="text-amber-700 dark:text-amber-400">📍 <a href={mapHrefFor(u.updated_location)} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600 dark:hover:text-blue-400">{u.updated_location}</a></div>)}
                                {u.updated_direction && (<div className="text-amber-600 dark:text-amber-400">→ {u.updated_direction}</div>)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
            {/* Location Updates History - Combined list of update locations + perp locations */}
            {(() => {
              // Combine location updates and perp locations into one list
              const locationUpdates = (call.updates || [])
                .filter(u => u.updated_location || u.updated_direction)
                .map(u => ({
                  type: 'update',
                  id: u.update_id,
                  timestamp: u.timestamp,
                  updatedBy: u.updated_by,
                  location: u.updated_location,
                  direction: u.updated_direction || null,
                  locationLabel: u.location_label || null,
                  perpId: u.perp_id,
                  perpNum: u.perp_id ? (call.perp_descriptions?.findIndex(p => p.perp_id === u.perp_id) + 1) || null : null
                }));

              // Use created_at for perp locations so they don't jump to top on unrelated edits
              // Filter out perps that already have location updates to avoid duplicates
              const perpsWithLocationUpdates = new Set(locationUpdates.map(u => u.perpId).filter(Boolean));
              const perpLocations = (call.perp_descriptions || [])
                .map((perp, idx) => ({ perp, perpNum: idx + 1 }))
                .filter(({ perp }) => perp.current_location || perp.direction_of_flight)
                .filter(({ perp }) => !perpsWithLocationUpdates.has(perp.perp_id))
                .map(({ perp, perpNum }) => ({
                  type: 'perp',
                  id: perp.perp_id,
                  timestamp: perp.created_at,
                  updatedBy: perp.created_by,
                  location: perp.current_location,
                  direction: perp.direction_of_flight,
                  perpId: perp.perp_id,
                  perpNum
                }));

              const allLocations = [...locationUpdates, ...perpLocations];

              if (allLocations.length === 0) return null;

              const sortedLocations = allLocations.sort((a, b) =>
                locationSortOrder === "newest"
                  ? new Date(b.timestamp) - new Date(a.timestamp)
                  : new Date(a.timestamp) - new Date(b.timestamp)
              );

              return (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2 gap-1">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Location Updates ({allLocations.length})</p>
                    <div className="flex items-center gap-1">
                      {showLocationUpdates && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onLocationSortChange && onLocationSortChange(locationSortOrder === "newest" ? "oldest" : "newest")}
                          className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          {locationSortOrder === "newest" ? (
                            <><ChevronDown className="w-3 h-3" /> New</>
                          ) : (
                            <><ChevronUp className="w-3 h-3" /> Old</>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowLocationUpdates(!showLocationUpdates)}
                        className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {showLocationUpdates ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                  </div>
                  {showLocationUpdates && (
                  <div className="space-y-1.5">
                    {sortedLocations.map((item) => {
                      // Identify the update target — perp, additional location, or
                      // fall back to "Original" so every row shows where it applies.
                      const targetBadge = item.perpNum
                        ? <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-600 text-white">Perp #{item.perpNum}</Badge>
                        : item.locationLabel
                          ? <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-blue-600 text-white">{item.locationLabel}</Badge>
                          : <Badge className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-amber-600 text-white">Original</Badge>;
                      return (
                      <div key={item.id} className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800/50">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                          <span className="text-amber-700 dark:text-amber-400 font-mono whitespace-nowrap">{formatTime(item.timestamp)}</span>
                          {targetBadge}
                          <span className="text-slate-500 dark:text-slate-400">by {item.updatedBy}</span>
                        </div>
                        {item.location && (
                          <a
                            href={mapHrefFor(item.location)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-amber-800 dark:text-amber-300 hover:underline break-words block mt-1"
                          >
                            📍 {item.location}
                          </a>
                        )}
                        {item.direction && (
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">→ {item.direction}</p>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
          )}
        </Card>

        {/* 3. General / Basic Description — perp status updates moved to
             Timeline Updates so this card shows description text only. */}
        {(responseTab === "all" || responseTab === "perps") && call.description && (
              <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    General / Basic Description
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowDescription(!showDescription)}>
                      <Eye className="w-3 h-3 mr-1" />{showDescription ? "Hide" : "Show"}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showDescription && (
                <CardContent className="pt-0 space-y-2">
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{call.description}</p>
                </CardContent>
                )}
              </Card>
        )}

        {/* 3. Perpetrators */}
        {(responseTab === "all" || responseTab === "perps") && call.perp_descriptions?.length > 0 && (
          <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Perpetrators ({call.perp_descriptions?.length || 0})
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowPerps(!showPerps)}>
                  <Eye className="w-3 h-3 mr-1" />{showPerps ? "Hide" : "Show"}
                </Button>
              </CardTitle>
            </CardHeader>
            {showPerps && (
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {call.perp_descriptions.map((perp, idx) => {
                  const perpUpdates = call.updates?.filter(u => u.perp_id === perp.perp_id && (u.updated_perp_description || u.updated_location || u.updated_direction)) || [];
                  return (
                    <div
                      key={perp.perp_id || idx}
                      className={`p-2 rounded border text-xs cursor-pointer ${perp.was_arrested ? 'bg-green-100 dark:bg-emerald-950 border-green-500 dark:border-emerald-800' : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700'}`}
                      onClick={() => setCollapsedPerps(prev => ({ ...prev, [perp.perp_id || idx]: !prev[perp.perp_id || idx] }))}
                    >
                      <div className="flex items-center gap-1 flex-wrap select-none">
                        {collapsedPerps[perp.perp_id || idx] ? <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" /> : <ChevronUp className="w-3 h-3 text-slate-500 shrink-0" />}
                        <span className="font-medium text-slate-700 dark:text-slate-300">#{idx + 1}</span>
                        {perp.name && <span className="text-slate-700 dark:text-slate-300 font-medium">{perp.name}</span>}
                        {perp.has_weapon && <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center">ARMED</Badge>}
                        {(() => {
                          // Same latest-only rule as LiveCalls / Location Info.
                          const candidates = [
                            (perp.was_arrested || perp.is_arrested) && { label: 'ARRESTED', cls: 'bg-green-600 text-white', ts: perp.was_arrested_at || perp.arrest_updated_at },
                            perp.is_holding && { label: 'HOLDING', cls: 'bg-orange-500 text-white', ts: perp.is_holding_at },
                            perp.is_detained && { label: 'DETAINED', cls: 'bg-amber-500 text-black', ts: perp.is_detained_at },
                            perp.is_visual && { label: 'VISUAL', cls: 'bg-cyan-500 text-white', ts: perp.is_visual_at },
                          ].filter(Boolean);
                          if (candidates.length === 0) return null;
                          candidates.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
                          const latest = candidates[0];
                          return <Badge className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center ${latest.cls}`}>{latest.label}</Badge>;
                        })()}
                      </div>
                      
                      {/* Content - collapsible */}
                      {!collapsedPerps[perp.perp_id || idx] && <div>
                      {/* Linked Profile - Clickable with profile name shown - FIX #1 and #4 */}
                      {perp.linked_profile_id && (
                        <div 
                          className="mb-2 p-1.5 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700/50 rounded flex items-center gap-2 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50"
                          onClick={(e) => { e.stopPropagation(); setShowProfilePopup({
                            profile_id: perp.linked_profile_id,
                            name: perp.linked_profile_name || perp.name || 'Linked Profile'
                          }); }}
                        >
                          <Link className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          <span className="text-purple-700 dark:text-purple-300 font-medium text-xs truncate">
                            {perp.linked_profile_name || perp.name || 'Unknown Profile'}
                          </span>
                        </div>
                      )}
                      
                      <div className="text-slate-600 dark:text-slate-400 space-y-0.5">
                        {/* WEAPON INFO FIRST - Most critical */}
                        {perp.has_weapon && (
                          <div className="text-red-600 dark:text-red-400 font-bold bg-red-500/20 px-1.5 py-0.5 rounded border border-red-500/50">
                            ⚠️ ARMED{perp.weapon_description ? `: ${perp.weapon_description}` : ''}
                          </div>
                        )}
                        {perp.weapon_description && !perp.has_weapon && (
                          <div className="text-red-600 dark:text-red-400 font-medium">⚠️ Weapon: {perp.weapon_description}</div>
                        )}
                        {/* Current Location & Direction - show which is newer */}
                        {(() => {
                          const locTime = perp.location_updated_at ? new Date(perp.location_updated_at) : null;
                          const dirTime = perp.direction_updated_at ? new Date(perp.direction_updated_at) : null;
                          // If timestamps are within 2 seconds, consider them sent together
                          const sameUpdate = locTime && dirTime && Math.abs(locTime.getTime() - dirTime.getTime()) < 2000;
                          const locationIsNewer = sameUpdate || (locTime && dirTime ? locTime > dirTime : locTime && !dirTime);
                          const directionIsNewer = sameUpdate || (dirTime && locTime ? dirTime > locTime : dirTime && !locTime);
                          const hasMultipleUpdates = perp.current_location && perp.direction_of_flight && (locTime || dirTime);

                          return (
                            <>
                              {hasMultipleUpdates && !sameUpdate && (
                                <p className="text-[9px] text-slate-500 dark:text-slate-400 mb-0.5 italic">Green = latest update</p>
                              )}
                              {perp.current_location && (
                                <div className={`flex items-center justify-between gap-1 px-1.5 py-0.5 rounded ${locationIsNewer ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium border border-green-300 dark:border-green-700' : 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'}`}>
                                  <span>📍 {perp.current_location}</span>
                                  {locationIsNewer && <Badge className="bg-green-600 text-white text-[8px] px-1 py-0">NEW</Badge>}
                                </div>
                              )}
                              {perp.direction_of_flight && (
                                <div className={`flex items-center justify-between gap-1 px-1.5 py-0.5 rounded ${directionIsNewer ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium border border-green-300 dark:border-green-700' : 'text-amber-700 dark:text-amber-400'}`}>
                                  <span>→ {perp.direction_of_flight}</span>
                                  {directionIsNewer && <Badge className="bg-green-600 text-white text-[8px] px-1 py-0">NEW</Badge>}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {/* Physical Description - values bold so they stand out vs the dim labels */}
                        {(perp.sex || perp.race || perp.age || perp.height || perp.weight || perp.build || perp.complexion || perp.hair_color_cut || perp.eye_color) && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                            {perp.sex && <span><span className="text-slate-500 dark:text-slate-500">Sex:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.sex}</span></span>}
                            {perp.race && <span><span className="text-slate-500 dark:text-slate-500">Race:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.race}</span></span>}
                            {perp.age && <span><span className="text-slate-500 dark:text-slate-500">Age:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.age}</span></span>}
                            {perp.height && <span><span className="text-slate-500 dark:text-slate-500">Height:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.height}</span></span>}
                            {perp.weight && <span><span className="text-slate-500 dark:text-slate-500">Weight:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.weight}</span></span>}
                            {perp.build && <span><span className="text-slate-500 dark:text-slate-500">Build:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.build}</span></span>}
                            {perp.complexion && <span><span className="text-slate-500 dark:text-slate-500">Complexion:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.complexion}</span></span>}
                            {perp.hair_color_cut && <span><span className="text-slate-500 dark:text-slate-500">Hair:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.hair_color_cut}</span></span>}
                            {perp.eye_color && <span><span className="text-slate-500 dark:text-slate-500">Eyes:</span> <span className="font-semibold text-slate-900 dark:text-slate-100">{perp.eye_color}</span></span>}
                          </div>
                        )}
                        {/* Clothing - bold each value, dim labels and separators */}
                        {(perp.hat_color_style || perp.shirt || perp.pants || perp.jacket_coat || perp.shoes || perp.clothing) && (() => {
                          const parts = [
                            perp.hat_color_style && { label: "Hat", value: perp.hat_color_style },
                            perp.shirt && { label: "Shirt", value: perp.shirt },
                            perp.jacket_coat && { label: "Jacket", value: perp.jacket_coat },
                            perp.pants && { label: "Pants", value: perp.pants },
                            perp.shoes && { label: "Shoes", value: perp.shoes },
                            perp.clothing && { label: null, value: perp.clothing },
                          ].filter(Boolean);
                          return (
                            <div>
                              <span className="text-slate-500">Clothing: </span>
                              {parts.map((p, i) => (
                                <span key={i}>
                                  {i > 0 && <span className="text-slate-500"> | </span>}
                                  {p.label && <span className="text-slate-500">{p.label}: </span>}
                                  <span className="font-semibold text-slate-900 dark:text-slate-100">{p.value}</span>
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                        {/* Visible Marks */}
                        {perp.visible_marks && <div className="text-purple-600 dark:text-purple-400"><span>Marks/Tattoos: </span><span className="font-semibold">{perp.visible_marks}</span></div>}
                        {/* Description */}
                        {perp.description && <div className="italic font-semibold text-slate-900 dark:text-slate-100">{perp.description}</div>}
                        {/* Notes */}
                        {perp.notes && <div className="font-semibold text-slate-900 dark:text-slate-100">{perp.notes}</div>}
                        {/* Vehicle Description */}
                        {perp.vehicle_description && (
                          <div className="text-blue-600 dark:text-blue-400">🚗 <span className="font-semibold">{perp.vehicle_description}</span> {perp.license_plate && <span className="font-semibold">({perp.license_plate})</span>}</div>
                        )}
                        {perp.license_plate && !perp.vehicle_description && (
                          <div className="text-blue-600 dark:text-blue-400"><span>Plate: </span><span className="font-semibold">{perp.license_plate}</span></div>
                        )}
                        {/* Address */}
                        {perp.address && (
                          <div className="text-slate-500"><span>Address: </span><span className="font-semibold text-slate-900 dark:text-slate-100">{perp.address}</span></div>
                        )}
                        {/* Updates for this perp */}
                        {perpUpdates.length > 0 && (() => {
                          const updKey = perp.perp_id || `idx_${idx}`;
                          const updatesHidden = !!collapsedPerpUpdates[updKey];
                          return (
                            <div className="mt-1 pt-1 border-t border-slate-200 dark:border-slate-700">
                              <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                                <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase font-medium">Updates ({perpUpdates.length})</p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-1.5 text-[10px] text-slate-500 hover:text-slate-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCollapsedPerpUpdates(prev => ({ ...prev, [updKey]: !prev[updKey] }));
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />{updatesHidden ? 'Show' : 'Hide'}
                                </Button>
                              </div>
                              {!updatesHidden && perpUpdates.map((u, uidx) => (
                                <div key={u.update_id || uidx} className="text-[11px] bg-purple-50 dark:bg-purple-900/20 rounded px-1 py-0.5 mt-0.5">
                                  <span className="text-slate-500 dark:text-slate-400 font-mono">{formatTime(u.timestamp)}</span>
                                  {u.updated_location && <span className="text-amber-600 dark:text-amber-400 ml-1">📍 {u.updated_location}</span>}
                                  {u.updated_direction && <span className="text-amber-500 dark:text-amber-400 ml-1">→ {u.updated_direction}</span>}
                                  {u.updated_perp_description && !(u.updated_location && /^PERP #\d+ U\/D: (Holding|Detained) location:/.test(u.updated_perp_description)) && <span className="text-purple-600 dark:text-purple-300 ml-1">{u.updated_perp_description}</span>}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      </div>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
            )}
          </Card>
        )}

        {/* 4. Updates Timeline */}
        {(responseTab === "all" || responseTab === "narrative") && (
          <div className="flex gap-1 max-w-2xl">
            <button
              type="button"
              onClick={() => setUpdatesTab("timeline")}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-colors ${updatesTab === "timeline" ? "bg-blue-600 text-white border-blue-500" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:border-slate-400"}`}
            >
              Timeline Updates
            </button>
            <button
              type="button"
              onClick={() => setUpdatesTab("all")}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded border transition-colors ${updatesTab === "all" ? "bg-blue-600 text-white border-blue-500" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:border-slate-400"}`}
            >
              All Updates ({allUpdates.length})
            </button>
          </div>
        )}

        {(responseTab === "all" || responseTab === "narrative") && updatesTab === "timeline" && (() => {
          // Timeline Updates includes free-text update_text entries PLUS perp
          // lifecycle status changes (arrest/holding/detained), which moved
          // from the Basic Description card so the call narrative + perp
          // status changes live in one chronological view.
          const isPerpStatusUpdate = (u) => {
            const t = u.updated_perp_description || "";
            return (
              t.includes('ARRESTED') ||
              t.includes('Arrest cleared') ||
              t.includes('HOLDING') ||
              t.includes('Holding cleared') ||
              t.includes('Holding location') ||
              t.includes('DETAINED') ||
              t.includes('Detained cleared') ||
              t.includes('Detained location')
            );
          };
          const updateTextEntries = call.updates?.filter(u => u.update_text || isPerpStatusUpdate(u)) || [];
          return (
              <Card className="bg-slate-50 dark:bg-slate-800 border-blue-200 dark:border-blue-800/50">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="text-blue-700 dark:text-blue-300">Updates (Timeline)</span>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowUpdatesTimeline(!showUpdatesTimeline)}>
                      <Eye className="w-3 h-3 mr-1" />{showUpdatesTimeline ? "Hide" : "Show"}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showUpdatesTimeline && (
                <CardContent className="pt-0 space-y-2 min-h-[80px]">
                  {updateTextEntries.length > 0 ? (
                    (updatesSortOrder === "newest" ? [...updateTextEntries].reverse() : updateTextEntries).map((update, idx) => {
                      const desc = update.updated_perp_description || "";
                      const isPerpStatus = isPerpStatusUpdate(update);
                      const borderClass = (
                        desc.includes('ARRESTED') ? 'border-green-500' :
                        desc.includes('Arrest cleared') ? 'border-green-500/50' :
                        (desc.includes('HOLDING') || desc.includes('Holding location')) ? 'border-orange-500' :
                        desc.includes('Holding cleared') ? 'border-orange-500/50' :
                        (desc.includes('DETAINED') || desc.includes('Detained location')) ? 'border-amber-500' :
                        desc.includes('Detained cleared') ? 'border-amber-500/50' :
                        'border-blue-500'
                      );
                      const bodyClass = (
                        desc.includes('ARRESTED') ? 'text-green-600 dark:text-green-400' :
                        desc.includes('Arrest cleared') ? 'text-green-600/70 dark:text-green-400/70' :
                        (desc.includes('HOLDING') || desc.includes('Holding location')) ? 'text-orange-600 dark:text-orange-400' :
                        desc.includes('Holding cleared') ? 'text-orange-600/70 dark:text-orange-400/70' :
                        (desc.includes('DETAINED') || desc.includes('Detained location')) ? 'text-amber-600 dark:text-amber-400' :
                        desc.includes('Detained cleared') ? 'text-amber-600/70 dark:text-amber-400/70' :
                        'text-blue-700 dark:text-blue-300'
                      );
                      const bodyText = update.update_text || (isPerpStatus ? update.updated_perp_description : "");
                      return (
                        <div key={update.update_id || idx} className={`border-l-2 ${borderClass} pl-3 py-1`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-mono">{formatTime(update.timestamp)}</span>
                            <span className="text-slate-500 dark:text-slate-400 text-xs">by {update.updated_by}</span>
                          </div>
                          <p className={`text-sm ${bodyClass}`}>{bodyText}</p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-slate-500 italic">No timeline notes</p>
                  )}
                </CardContent>
                )}
              </Card>
            );
          })()}

        {/* All Updates — rendered in the same slot as the Timeline card when its tab is selected */}
        {(responseTab === "all" || responseTab === "narrative") && updatesTab === "all" && allUpdates.length > 0 && (() => {
          const filtered = [...allUpdates]
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .filter(update => {
              if (!updatesSearch.trim()) return true;
              const q = updatesSearch.toLowerCase();
              return (
                (update.update_text || "").toLowerCase().includes(q) ||
                (update.general_notes || "").toLowerCase().includes(q) ||
                (update.updated_location || "").toLowerCase().includes(q) ||
                (update.updated_direction || "").toLowerCase().includes(q) ||
                (update.updated_perp_description || "").toLowerCase().includes(q) ||
                (update.updated_by || "").toLowerCase().includes(q) ||
                (update.action || "").toLowerCase().includes(q)
              );
            });
          // Determine an accent color per row from the most prominent field present.
          const accentFor = (u) => {
            if (u.updated_perp_description) return "border-l-purple-500";
            if (u.updated_location || u.updated_direction) return "border-l-amber-500";
            if (u.action) return "border-l-emerald-500";
            if (u.general_notes) return "border-l-slate-500";
            return "border-l-blue-500";
          };
          // Type metadata: icon, label, dot color, panel theme.
          const typeFor = (u) => {
            if (u.updated_perp_description)
              return { label: "Perp",     dot: "bg-purple-500",  panel: "bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800/40" };
            if (u.updated_location || u.updated_direction)
              return { label: "Location", dot: "bg-amber-500",   panel: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/40" };
            if (u.action)
              return { label: "Action",   dot: "bg-emerald-500", panel: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/40" };
            if (u.general_notes)
              return { label: "Note",     dot: "bg-slate-400",   panel: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700" };
            return     { label: "Update",   dot: "bg-blue-500",    panel: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/40" };
          };
          return (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm">
              <CardHeader className="py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800/60 rounded-t-lg">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                    All Updates
                    <Badge className="bg-slate-600 text-white text-[10px] px-1.5 py-0">
                      {filtered.length}{updatesSearch ? `/${allUpdates.length}` : ""}
                    </Badge>
                  </div>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    placeholder="Search updates..."
                    value={updatesSearch}
                    onChange={(e) => setUpdatesSearch(e.target.value)}
                    className="h-8 pl-8 pr-8 text-xs bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 focus-visible:ring-1 focus-visible:ring-blue-500"
                  />
                  {updatesSearch && (
                    <button
                      type="button"
                      onClick={() => setUpdatesSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3 bg-slate-50 dark:bg-slate-950 rounded-b-lg">
                {filtered.length === 0 ? (
                  <div className="text-center text-xs text-slate-500 py-10">
                    No updates match "{updatesSearch}"
                  </div>
                ) : (
                  <ScrollArea className="h-[360px]">
                    <ol className="space-y-2 pr-2">
                      {filtered.map((update, idx) => {
                        const perpIndex = update.perp_id ? (call.perp_descriptions?.findIndex(p => p.perp_id === update.perp_id) + 1) || null : null;
                        const meta = typeFor(update);
                        return (
                          <li
                            key={update.update_id || idx}
                            className={`p-2.5 rounded-md border ${meta.panel} shadow-sm`}
                          >
                            {/* Header row: type dot + label, optional perp/action chips, author + time */}
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} />
                                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">
                                  {meta.label}
                                </span>
                                {perpIndex && (
                                  <Badge className="bg-purple-600 text-white text-[9px] px-1.5 py-0">PERP #{perpIndex}</Badge>
                                )}
                                {/* Location-update target badge — Original or
                                    Additional title — only when this row is a
                                    location update with no perp (perp targets
                                    already get the PERP # badge above). */}
                                {!perpIndex && (update.updated_location || update.updated_direction) && (
                                  update.location_label
                                    ? <Badge className="bg-blue-600 text-white text-[9px] px-1.5 py-0">{update.location_label}</Badge>
                                    : <Badge className="bg-amber-600 text-white text-[9px] px-1.5 py-0">ORIGINAL</Badge>
                                )}
                                {update.action && (
                                  <Badge className="bg-emerald-600 text-white text-[9px] px-1.5 py-0 capitalize">
                                    {update.action.replace(/_/g, " ")}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0 text-[10px] text-slate-500 dark:text-slate-400">
                                {update.updated_by && <span className="truncate max-w-[80px]">{update.updated_by}</span>}
                                <span className="font-mono">{formatTime(update.timestamp)}</span>
                              </div>
                            </div>

                            {/* Body */}
                            <div className="space-y-0.5">
                              {update.update_text && (
                                <p className="text-sm text-slate-800 dark:text-slate-200 break-words leading-snug">
                                  {update.update_text}
                                </p>
                              )}
                              {update.general_notes && (
                                <p className="text-xs text-slate-600 dark:text-slate-300 italic break-words">
                                  {update.general_notes}
                                </p>
                              )}
                              {update.updated_location && (
                                <p className="text-xs text-amber-700 dark:text-amber-300 break-words flex items-start gap-1">
                                  <span>📍</span>
                                  <span className="flex-1">
                                    {update.updated_location}
                                    {update.location_label && (
                                      <Badge className="ml-1.5 text-[9px] bg-blue-600 text-white px-1 py-0 align-middle">{update.location_label}</Badge>
                                    )}
                                  </span>
                                </p>
                              )}
                              {update.updated_direction && (
                                <p className="text-xs text-amber-700 dark:text-amber-300 break-words flex items-start gap-1">
                                  <span>→</span>
                                  <span className="flex-1">
                                    {update.updated_direction}
                                    {!update.updated_location && update.location_label && (
                                      <Badge className="ml-1.5 text-[9px] bg-blue-600 text-white px-1 py-0 align-middle">{update.location_label}</Badge>
                                    )}
                                  </span>
                                </p>
                              )}
                              {update.updated_perp_description && (
                                <p className="text-xs text-purple-700 dark:text-purple-200 break-words">
                                  {update.updated_perp_description.match(/^PERP #\d|^Perp #\d/i)
                                    ? update.updated_perp_description
                                    : `${perpIndex ? `Perp #${perpIndex}: ` : ""}${update.updated_perp_description}`}
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* 6. Responding Units - with Patrol Car info */}
        {(responseTab === "all" || responseTab === "units") && (call.assigned_units?.length > 0 || (call.patrol_cars_assigned?.length > 0)) && (
          <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Users className="w-4 h-4" />
                  Responding Units ({call.assigned_units?.length || 0})
                  {(call.patrol_cars_details || []).map((car) => (
                    <Badge key={car.car_id} className="text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center bg-purple-600 text-white">
                      🚓 {car.car_name}
                    </Badge>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowRespondingUnits(!showRespondingUnits)}>
                  <Eye className="w-3 h-3 mr-1" />{showRespondingUnits ? "Hide" : "Show"}
                </Button>
              </CardTitle>
            </CardHeader>
            {showRespondingUnits && (
            <CardContent className="pt-0 pb-2">
              <div className="flex flex-wrap items-center gap-1">
                {[...(call.assigned_units || [])].sort((a, b) => {
                  const numA = parseInt(a.replace(/\D/g, '')) || 0;
                  const numB = parseInt(b.replace(/\D/g, '')) || 0;
                  if (numA !== numB) return numA - numB;
                  return a.localeCompare(b);
                }).map((unit) => {
                  const status = call.unit_statuses?.[unit] || {};
                  const isMe = unit === myUnit;
                  return (
                    <Badge
                      key={unit}
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0.5 inline-flex items-center justify-center ${isMe ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300' : 'bg-white dark:bg-slate-700 border-slate-400 dark:border-slate-600 text-slate-700 dark:text-slate-300'}`}
                    >
                      {unit}
                      {status.status_standby ? (
                        <span className="ml-1 text-amber-600 dark:text-amber-400 font-semibold">STBY</span>
                      ) : (
                        <>
                          {status.status_84 && <span className="ml-1 text-blue-600 dark:text-blue-400">84</span>}
                          {status.status_98 && <span className="ml-1 text-green-600 dark:text-green-400">98</span>}
                        </>
                      )}
                      {isMe && <span className="ml-1 text-purple-600 dark:text-purple-400">YOU</span>}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
            )}
          </Card>
        )}

        {/* 7. Media Files - with audio/video recording options (requirements #2 and #3) */}
        <Card className={`bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 ${responseTab !== "all" && responseTab !== "media" ? "hidden" : ""}`}>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Image className="w-4 h-4" />
                Media Files ({mediaFiles?.length || 0})
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowMedia(!showMedia)}>
                  <Eye className="w-3 h-3 mr-1" />{showMedia ? "Hide" : "Show"}
                </Button>
                {/* Upload Files Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMediaUpload(call.call_id)}
                  disabled={isUploadingMedia}
                  className="text-xs"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {isUploadingMedia ? 'Uploading...' : 'Upload'}
                </Button>

                {/* Record Video Button (requirement #3) */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Create file input that captures video
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'video/*';
                    input.capture = 'environment'; // Use rear camera
                    input.onchange = async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Upload the recorded video
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const uploadResponse = await axios.post(
                            `${API}/upload`,
                            formData,
                            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                          );
                          if (uploadResponse.data.url) {
                            await axios.post(
                              `${API}/live-calls/${call.call_id}/media`,
                              { media_files: [uploadResponse.data.url] },
                              { withCredentials: true }
                            );
                            setSelectedCallMedia(prev => [...prev, uploadResponse.data.url]);
                            toast.success('Video uploaded');
                          }
                        } catch (err) {
                          toast.error('Failed to upload video');
                        }
                      }
                    };
                    input.click();
                  }}
                  className="text-xs"
                >
                  <Video className="w-3 h-3 mr-1" />
                  Record Video
                </Button>
                
                {/* Record Audio Button - In-app recording */}
                <Button
                  size="sm"
                  variant={isRecording ? "destructive" : "outline"}
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`text-xs ${isRecording ? 'animate-pulse' : ''}`}
                >
                  {isRecording ? (
                    <>
                      <Square className="w-3 h-3 mr-1" />
                      Stop ({Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')})
                    </>
                  ) : (
                    <>
                      <Mic className="w-3 h-3 mr-1" />
                      Record Audio
                    </>
                  )}
                </Button>
                
                {/* Take Photo Button - Camera capture */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment'; // Use rear camera
                    input.onchange = async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const uploadResponse = await axios.post(
                            `${API}/upload`,
                            formData,
                            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                          );
                          if (uploadResponse.data.url) {
                            await axios.post(
                              `${API}/live-calls/${call.call_id}/media`,
                              { media_files: [uploadResponse.data.url] },
                              { withCredentials: true }
                            );
                            setSelectedCallMedia(prev => [...prev, uploadResponse.data.url]);
                            toast.success('Photo uploaded');
                          }
                        } catch (err) {
                          toast.error('Failed to upload photo');
                        }
                      }
                    };
                    input.click();
                  }}
                  className="text-xs"
                >
                  <Image className="w-3 h-3 mr-1" />
                  Camera
                </Button>
              </div>
            </div>
          </CardHeader>
          {/* Upload Progress Bar */}
          {isUploadingMedia && (
            <div className="flex items-center gap-2 px-4 pb-3">
              <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400 min-w-[40px]">{uploadProgress}%</span>
            </div>
          )}
          {showMedia && (
          <CardContent className="pt-0">
            {loadingMedia ? (
              <p className="text-sm text-slate-500 italic">Loading media...</p>
            ) : mediaFiles?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {mediaFiles.map((media, idx) => {
                  // Handle both base64 and URL formats
                  const isBase64 = media && media.startsWith('data:');
                  // For URLs, check the path or content type
                  const mediaLower = (media || '').toLowerCase();
                  const isImage = isBase64 
                    ? media.startsWith('data:image') 
                    : /\.(jpg|jpeg|png|gif|webp|bmp)($|\?)/i.test(media) || mediaLower.includes('image');
                  const isVideo = isBase64 
                    ? media.startsWith('data:video') 
                    : /\.(mp4|webm|mov|avi)($|\?)/i.test(media) || mediaLower.includes('video');
                  const isAudio = isBase64
                    ? media.startsWith('data:audio')
                    : /\.(mp3|wav|ogg|m4a|aac|webm)($|\?)/i.test(media) || mediaLower.includes('audio');
                  
                  // Build the full URL - handle relative paths
                  let mediaSrc = media;
                  if (!isBase64 && media) {
                    if (media.startsWith('http://') || media.startsWith('https://')) {
                      mediaSrc = media;
                    } else if (media.startsWith('/api/')) {
                      // Replace /api/ with API base
                      mediaSrc = API.replace('/api', '') + media;
                    } else if (media.startsWith('/')) {
                      mediaSrc = API.replace('/api', '') + media;
                    } else {
                      mediaSrc = `${API}/uploads/${media}`;
                    }
                  }
                  
                  return (
                    <div
                      key={idx}
                      className="relative group"
                    >
                      {isImage ? (
                        <div className="relative w-full h-24 rounded border border-slate-300 dark:border-slate-700 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openLightbox(mediaSrc, 'image')}
                        >
                          <img
                            src={mediaSrc}
                            alt={`Media ${idx + 1}`}
                            className="w-full h-full object-cover pointer-events-none"
                            draggable="false"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {/* Server-side watermarking handles non-admin users */}
                        </div>
                      ) : isVideo ? (
                        <div
                          className="relative w-full h-24 bg-slate-900 rounded border border-slate-300 dark:border-slate-700 overflow-hidden cursor-pointer"
                          onClick={() => openLightbox(mediaSrc, 'video')}
                        >
                          <video
                            src={mediaSrc}
                            className="w-full h-full object-cover pointer-events-none"
                            muted
                            playsInline
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              e.target.currentTime = 1;
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                            <div className="bg-black/50 rounded-full p-1.5">
                              <Play className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          {/* Server-side watermarking handles non-admin users */}
                        </div>
                      ) : isAudio ? (
                        <div 
                          className="w-full h-24 bg-slate-200 dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center p-2 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-800 transition-colors"
                          onClick={() => openLightbox(mediaSrc, 'audio')}
                        >
                          <Mic className="w-6 h-6 text-blue-500 mb-1" />
                          <span className="text-xs text-slate-500">Audio Recording</span>
                          <span className="text-[10px] text-blue-500 mt-1">Tap to play</span>
                        </div>
                      ) : null}
                      {/* Fallback for failed images or unknown types */}
                      <div
                        className={`w-full h-24 bg-slate-200 dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-700 flex-col items-center justify-center cursor-pointer ${isImage || isVideo || isAudio ? 'hidden' : 'flex'}`}
                        onClick={() => onDownloadMedia(mediaSrc, idx)}
                      >
                        <FileText className="w-6 h-6 text-slate-500" />
                        <span className="text-[10px] text-slate-500 mt-1">Tap to download</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No media files</p>
            )}
          </CardContent>
          )}
        </Card>

        {/* Unit Call Notes — only on All / Narrative tabs */}
        {isAssigned && (responseTab === "all" || responseTab === "narrative") && (
          <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-2xl">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Unit Notes {call.unit_notes?.length > 0 && `(${call.unit_notes.length})`}
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowUnitNotes(!showUnitNotes)}>
                  <Eye className="w-3 h-3 mr-1" />{showUnitNotes ? "Hide" : "Show"}
                </Button>
              </CardTitle>
            </CardHeader>
            {showUnitNotes && (
            <CardContent className="pt-0 space-y-2">
              {/* Existing unit notes */}
              {call.unit_notes?.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {call.unit_notes.map((un) => (
                    <div key={un.note_id} className="p-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{un.unit}</span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {un.timestamp ? new Date(un.timestamp).toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
                          }) : ""}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{un.note}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* Add note */}
              <div className="flex gap-2">
                <Textarea
                  value={unitNoteText}
                  onChange={(e) => setUnitNoteText(e.target.value)}
                  placeholder={`Add a note as ${myUnit}...`}
                  className="flex-1 min-h-[60px] text-sm bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
                  rows={2}
                />
              </div>
              <Button
                size="sm"
                disabled={!unitNoteText.trim() || submittingUnitNote}
                onClick={async () => {
                  if (!unitNoteText.trim()) return;
                  setSubmittingUnitNote(true);
                  try {
                    const res = await axios.post(`${API}/live-calls/${call.call_id}/unit-note`, {
                      note: unitNoteText.trim()
                    }, { withCredentials: true });
                    setSelectedCall(res.data);
                    setUnitNoteText("");
                    toast.success("Note added");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to add note");
                  } finally {
                    setSubmittingUnitNote(false);
                  }
                }}
              >
                {submittingUnitNote ? "Submitting..." : "Submit Note"}
              </Button>
            </CardContent>
            )}
          </Card>
        )}

        {/* Profile Popup Dialog - FIX #1: Proper navigation */}
        {showProfilePopup && (
          <Dialog open={!!showProfilePopup} onOpenChange={() => setShowProfilePopup(null)}>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-purple-400">{showProfilePopup.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-300">View perpetrator profile?</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      // Navigate within the app
                      onNavigateToProfile(showProfilePopup.profile_id);
                      setShowProfilePopup(null);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Go to Profile
                  </Button>
                  <Button variant="ghost" onClick={() => setShowProfilePopup(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Media Lightbox */}
        {lightboxMedia && (
          <ImageLightbox 
            src={lightboxMedia}
            mediaType={lightboxMediaType}
            alt="Call media"
            onClose={() => {
              setLightboxMedia(null);
              setLightboxMediaType(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
export default function Response() {
  const { user } = useAuth();
  const { handlePhoneClick } = useSipPhone() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [liveCalls, setLiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Sort state
  const [sortOrder, setSortOrder] = useState("newest");

  // Desktop list minimized state (to maximize call details view)
  const [listMinimized, setListMinimized] = useState(false);

  // Ref to track known call IDs for notification comparison
  const knownCallIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);
  
  // Ref to track currently selected call ID (stable across renders)
  const selectedCallIdRef = useRef(null);
  
  // Track if we've processed the URL call_id parameter
  const processedUrlCallIdRef = useRef(false);
  
  // Real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Media upload state
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCallMedia, setSelectedCallMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // Updates sort order
  const [updatesSortOrder, setUpdatesSortOrder] = useState("newest");
  const [locationSortOrder, setLocationSortOrder] = useState("newest");

  // Get the user's linked unit (this IS their identity/name)
  const myUnit = user?.linked_unit || null;

  // Handle call_id from URL query parameter (e.g., from push notification)
  useEffect(() => {
    const callIdFromUrl = searchParams.get('call_id');
    if (callIdFromUrl && !processedUrlCallIdRef.current && liveCalls.length > 0) {
      const callToSelect = liveCalls.find(c => c.call_id === callIdFromUrl);
      if (callToSelect) {
        console.log('Auto-selecting call from URL:', callIdFromUrl);
        setSelectedCall(callToSelect);
        selectedCallIdRef.current = callIdFromUrl;
        toast.info(`Opened call: ${callToSelect.location || callToSelect.call_type}`);
        // Clear the URL parameter after processing
        setSearchParams({});
      }
      processedUrlCallIdRef.current = true;
    }
  }, [searchParams, liveCalls, setSearchParams]);

  // Handle mobile back button for call detail view
  const prepareNavigate = useBackButton(selectedCall !== null, () => setSelectedCall(null));

  // Fetch media when a call is selected
  useEffect(() => {
    if (selectedCall?.call_id) {
      setLoadingMedia(true);
      axios.get(`${API}/live-calls/${selectedCall.call_id}/media`, { withCredentials: true })
        .then(res => {
          setSelectedCallMedia(res.data.media_files || []);
        })
        .catch(err => {
          console.error("Failed to fetch media:", err);
          setSelectedCallMedia([]);
        })
        .finally(() => {
          setLoadingMedia(false);
        });
    } else {
      setSelectedCallMedia([]);
    }
  }, [selectedCall?.call_id]);

  const fetchData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(`${API}/live-calls`, { withCredentials: true });
      
      // Capture the current selected call ID from ref (stable across renders)
      const currentSelectedId = selectedCallIdRef.current;
      
      setLiveCalls(response.data);
      
      // Update selected call if it exists - use the ref to find the updated call
      if (currentSelectedId) {
        const updated = response.data.find(c => c.call_id === currentSelectedId);
        if (updated) {
          setSelectedCall(updated);
        }
        // If not found, keep current selection
      }
      
      // Check for NEW calls (not on initial load)
      const currentCallIds = new Set(response.data.map(c => c.call_id));
      const newCalls = response.data.filter(c => !knownCallIdsRef.current.has(c.call_id));
      
      // Mark new calls as delivered (parallel, non-blocking)
      if (newCalls.length > 0) {
        // Don't await - send all in parallel and don't block page load
        Promise.all(
          newCalls.map(call =>
            axios.post(`${API}/live-calls/${call.call_id}/delivered`, {}, { withCredentials: true })
              .catch(() => {}) // Silently fail - not critical
          )
        );
      }
      
      // Update known call IDs
      knownCallIdsRef.current = currentCallIds;
      
      // Notify for new calls (not initial load)
      if (!isInitialLoadRef.current && newCalls.length > 0) {
        const notifSettings = JSON.parse(localStorage.getItem('notificationSettings') || '{}');
        const notificationsEnabled = notifSettings.enabled !== false;
        
        if (notificationsEnabled) {
          toast.info(`${newCalls.length} new live call${newCalls.length > 1 ? 's' : ''} received!`, {
            duration: 5000
          });
          
          // Vibration for mobile
          if ('vibrate' in navigator) {
            try {
              navigator.vibrate([200, 100, 200]);
            } catch (e) {
              // Vibration not supported
            }
          }
          
          // Audio notification
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch (e) {
            // Audio notification failed
          }
          // Note: Push notifications are handled by service worker, no need for browser notification here
        }
      }
      
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error("Failed to fetch live calls:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Real-time clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Keep ref in sync with selectedCall state
  useEffect(() => {
    selectedCallIdRef.current = selectedCall?.call_id || null;
  }, [selectedCall]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    
    // Request notification permission
    if (typeof Notification !== 'undefined' && Notification.permission === "default") {
      Notification.requestPermission().catch(console.error);
    }
    
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handler for responding to a call (self-assign)
  const handleRespond = async (callId) => {
    if (!myUnit) {
      toast.error("You don't have a linked unit. Contact admin.");
      return;
    }
    
    try {
      await axios.post(
        `${API}/live-calls/${callId}/respond`,
        {},
        { withCredentials: true }
      );
      
      // Refresh data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      setLiveCalls(refreshed.data);
      const updatedCall = refreshed.data.find(c => c.call_id === callId);
      if (updatedCall) {
        setSelectedCall(updatedCall);
      }
      
      toast.success(`${myUnit} responding to call`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond to call");
    }
  };

  // Handler for un-responding from a call (self-unassign)
  const handleUnrespond = async (callId) => {
    if (!myUnit) return;
    
    try {
      await axios.post(
        `${API}/live-calls/${callId}/unrespond`,
        {},
        { withCredentials: true }
      );
      
      // Refresh data
      const refreshed = await axios.get(`${API}/live-calls`, { withCredentials: true });
      setLiveCalls(refreshed.data);
      const updatedCall = refreshed.data.find(c => c.call_id === callId);
      if (updatedCall) {
        setSelectedCall(updatedCall);
      }
      
      toast.success(`${myUnit} no longer responding`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unrespond from call");
    }
  };

  // Mark call as read when viewing - debounced to avoid rapid fire
  const markCallAsReadRef = useRef(null);
  const markCallAsRead = useCallback(async (callId) => {
    // Clear any pending call
    if (markCallAsReadRef.current) {
      clearTimeout(markCallAsReadRef.current);
    }
    
    // Debounce by 500ms to handle rapid switching
    markCallAsReadRef.current = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${API}/live-calls/${callId}/read`,
          { source: "response" },
          { withCredentials: true }
        );
        // Only update if we're still on this call
        setSelectedCall(prev => {
          if (prev?.call_id === callId) {
            return { ...prev, read_receipts: response.data.read_receipts };
          }
          return prev;
        });
        setLiveCalls(prev => prev.map(c => 
          c.call_id === callId ? { ...c, read_receipts: response.data.read_receipts } : c
        ));
      } catch (error) {
        console.log('Failed to mark as read:', error);
      }
    }, 500);
  }, []);

  // Mark selected call as read when it changes
  const lastMarkedCallRef = useRef(null);
  useEffect(() => {
    if (selectedCall?.call_id && selectedCall.call_id !== lastMarkedCallRef.current) {
      lastMarkedCallRef.current = selectedCall.call_id;
      markCallAsRead(selectedCall.call_id);
    }
  }, [selectedCall?.call_id, markCallAsRead]);

  // Handler for updating my unit's 84/98 status
  const handleUpdateMyStatus = async (callId, status84, status98) => {
    if (!myUnit) {
      toast.error("You don't have a linked unit");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/live-calls/${callId}/my-unit-status`,
        { status_84: status84, status_98: status98 },
        { withCredentials: true }
      );
      
      setSelectedCall(response.data);
      setLiveCalls(liveCalls.map(c => c.call_id === callId ? response.data : c));
      
      const statusText = [];
      if (status84) statusText.push('84');
      if (status98) statusText.push('98');
      toast.success(`${myUnit} marked as ${statusText.length > 0 ? statusText.join(', ') : 'cleared'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  // Helper function to capture video frame as thumbnail
  const captureVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      let hasResolved = false;
      const cleanup = () => {
        if (!hasResolved) {
          hasResolved = true;
          URL.revokeObjectURL(video.src);
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 10000);

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            cleanup();
            resolve(blob);
          }, 'image/jpeg', 0.85);
        } catch (err) {
          cleanup();
          resolve(null);
        }
      };

      video.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  // Handler for media upload - FIX #2: Upload to R2 via /api/upload endpoint
  const handleMediaUpload = async (callId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setIsUploadingMedia(true);
      setUploadProgress(0);

      try {
        // Upload files to R2 via /api/upload endpoint
        const uploadedUrls = [];
        const totalFiles = files.length;

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const isVideoFile = file.type.startsWith('video/');
          const formData = new FormData();
          formData.append('file', file);

          const uploadResponse = await axios.post(
            `${API}/upload`,
            formData,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
                const overallProgress = ((i / totalFiles) * 100) + (fileProgress / totalFiles);
                setUploadProgress(Math.round(overallProgress));
              }
            }
          );

          // Get the URL from response
          if (uploadResponse.data.url) {
            uploadedUrls.push(uploadResponse.data.url);

            // If it's a video, capture and upload thumbnail
            if (isVideoFile && uploadResponse.data.file_id) {
              console.log('[Response] Video upload - attempting thumbnail capture for:', uploadResponse.data.file_id);
              try {
                const thumbnailBlob = await captureVideoThumbnail(file);
                console.log('[Response] Thumbnail blob:', thumbnailBlob ? `${thumbnailBlob.size} bytes` : 'null');
                if (thumbnailBlob) {
                  const thumbFormData = new FormData();
                  thumbFormData.append('file', thumbnailBlob, 'thumbnail.jpg');
                  const thumbResponse = await axios.post(
                    `${API}/upload/${uploadResponse.data.file_id}/thumbnail`,
                    thumbFormData,
                    { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  console.log('[Response] Thumbnail uploaded:', thumbResponse.data);
                } else {
                  console.warn('[Response] Thumbnail capture returned null');
                }
              } catch (thumbError) {
                console.error("[Response] Thumbnail upload failed:", thumbError);
              }
            } else if (isVideoFile) {
              console.warn('[Response] Video file but no file_id in response:', uploadResponse.data);
            }
          }

          // Update progress after each file completes
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        }

        // Now link the uploaded files to the live call
        if (uploadedUrls.length > 0) {
          const response = await axios.post(
            `${API}/live-calls/${callId}/media`,
            { media_files: uploadedUrls },
            { withCredentials: true }
          );

          setSelectedCall(response.data);
          setLiveCalls(liveCalls.map(c => c.call_id === callId ? response.data : c));
          // Update selectedCallMedia with the new files
          setSelectedCallMedia(prev => [...prev, ...uploadedUrls]);

          toast.success(`Uploaded ${files.length} file(s)`);
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(error.response?.data?.detail || "Failed to upload media");
      } finally {
        setIsUploadingMedia(false);
        setUploadProgress(0);
      }
    };

    input.click();
  };

  // Download media file to device
  const handleDownloadMedia = async (mediaUrl, index) => {
    try {
      // Check if it's a video and user is non-admin
      const isVideoFile = /\.(mp4|webm|mov|avi|mkv)($|\?)/i.test(mediaUrl) ||
        mediaUrl.includes('video');
      if (isVideoFile && user?.role !== "admin") {
        toast.error("Video download is not available");
        return;
      }

      toast.info("Downloading...");

      // ALWAYS route through backend API to get watermarked files for non-admin
      // Extract filename from URL
      let filename = mediaUrl.split('/').pop().split('?')[0];
      let downloadUrl = `${API}/uploads/${filename}?download=true`;

      // Fetch through backend (applies watermarks for non-admin)
      const response = await fetch(downloadUrl, { credentials: 'include' });
      const blob = await response.blob();

      // Get file extension from URL or content type
      const contentType = response.headers.get('content-type') || '';
      let ext = 'jpg';
      if (contentType.includes('png') || mediaUrl.includes('.png')) ext = 'png';
      else if (contentType.includes('gif') || mediaUrl.includes('.gif')) ext = 'gif';
      else if (contentType.includes('mp4') || mediaUrl.includes('.mp4')) ext = 'mp4';
      else if (contentType.includes('webm') || mediaUrl.includes('.webm')) ext = 'webm';
      else if (contentType.includes('mov') || mediaUrl.includes('.mov')) ext = 'mov';
      else if (contentType.includes('pdf') || mediaUrl.includes('.pdf')) ext = 'pdf';

      // Create download filename
      const timestamp = Date.now();
      const downloadFilename = `CHSP_${timestamp}_${index + 1}.${ext}`;

      // Create blob URL and download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success("Downloaded!");
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed");
    }
  };

  // Mark call as read (triggered when user shows responding units)
  const handleMarkRead = async (callId) => {
    try {
      await axios.post(`${API}/live-calls/${callId}/read`, {}, { withCredentials: true });
    } catch (err) {
      // Silently fail - read receipt is not critical
      console.error("Failed to mark as read:", err);
    }
  };

  // Sort calls and filter out blocked ones
  const sortedCalls = [...liveCalls]
    .filter(c => !c.block_from_response) // Filter out blocked calls
    .sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);

    // Force top calls always first
    if (a.force_top_response && !b.force_top_response) return -1;
    if (b.force_top_response && !a.force_top_response) return 1;

    return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Response</h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            {myUnit ? <>Unit: <span className="text-blue-400 font-semibold">{myUnit}</span> | </> : ""}
            {liveCalls.length} active call{liveCalls.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <div className="text-sm text-slate-400 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="h-[calc(100%-60px)] flex gap-4 overflow-hidden">
        {/* Call List */}
        <div className={`${selectedCall ? 'hidden lg:block' : ''} transition-all duration-300 ease-in-out ${listMinimized ? 'lg:w-0 lg:opacity-0 lg:overflow-hidden lg:p-0 lg:border-0' : 'lg:opacity-100 w-full lg:w-1/3'} min-w-0 bg-slate-100 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden`}>
          <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <h2 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-500 animate-pulse" />
              Dispatch
            </h2>
            <button
              onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
              className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
              title={sortOrder === "newest" ? "Newest first" : "Oldest first"}
            >
              {sortOrder === "newest" ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
          
          <ScrollArea className="h-[calc(100%-50px)]">
            <div className="overflow-hidden pb-4">
            {sortedCalls.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Radio className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p>No active live calls</p>
              </div>
            ) : (
              sortedCalls.map((call, index) => (
                <CallListItem
                  key={call.call_id}
                  call={call}
                  selectedCall={selectedCall}
                  setSelectedCall={setSelectedCall}
                  myUnit={myUnit}
                  currentTime={currentTime}
                  onMarkRead={handleMarkRead}
                  callIndex={index}
                  totalCalls={sortedCalls.length}
                />
              ))
            )}
            </div>
          </ScrollArea>
        </div>

        {/* Call Detail Panel */}
        <div className={`${!selectedCall ? 'hidden lg:flex lg:items-center lg:justify-center' : ''} transition-all duration-300 ease-in-out ${listMinimized ? 'lg:w-full' : ''} flex-1 bg-slate-100 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden`}>
          {selectedCall ? (
            <CallDetail
              call={selectedCall}
              myUnit={myUnit}
              user={user}
              isUploadingMedia={isUploadingMedia}
              uploadProgress={uploadProgress}
              onMediaUpload={handleMediaUpload}
              onDownloadMedia={handleDownloadMedia}
              setSelectedCall={setSelectedCall}
              onRespond={handleRespond}
              onUnrespond={handleUnrespond}
              onUpdateStatus={handleUpdateMyStatus}
              currentTime={currentTime}
              updatesSortOrder={updatesSortOrder}
              onUpdatesSortChange={setUpdatesSortOrder}
              locationSortOrder={locationSortOrder}
              onLocationSortChange={setLocationSortOrder}
              mediaFiles={selectedCallMedia}
              loadingMedia={loadingMedia}
              setSelectedCallMedia={setSelectedCallMedia}
              onNavigateToProfile={(profileId) => { prepareNavigate(); navigate(`/perpetrators?profile=${profileId}`); }}
              listMinimized={listMinimized}
              setListMinimized={setListMinimized}
              handlePhoneClick={handlePhoneClick}
            />
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px] text-slate-500 dark:text-slate-400">
              <div className="text-center">
                <Radio className="w-16 h-16 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                <p className="text-lg">Select a call to view details</p>
                <p className="text-sm mt-2">Click on a call from the list to respond</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox 
          src={lightboxImage} 
          alt="Call media"
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}
